/*
 *
 * FurniturePanel
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { RootState } from './types';
import Panel from 'components/Panel';
import { makeSelectDisplayFurniturePanel } from 'containers/FurniturePanel/selectors';
import { makeSelectFurniture } from 'containers/Configurator/selectors/furniture';
import {
  removeCabinetAction,
  reorderingFurnitureAction,
  updateCabinetSuccess,
} from 'containers/Configurator/actions/furniture';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import FurnitureItem from 'containers/FurniturePanel/FurnitureItem';
import { FiCopy, FiTrash } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { css } from 'styled-components';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import useMedia from 'use-media';
import FurniturePanelMobile from './FurniturePanelMobile';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';
import Modal from 'react-responsive-modal';
import Img from '../../components/Img';
import ModalConfirmButton from '../../components/styles/ModalConfirmButton';
import ModalCancelButton from '../../components/styles/ModalCancelButton';
import { setColorsPanelAction } from '../Configurator/actions/general';
import { ActivePanel } from '../Configurator/constants/general';
import BigSelector from '../../components/BigSelector';
import whiteUS from '../../assets/images/ribbons/cabinetRibbon/upperShelf/white.png';
import { BLACK_COLOR, NATURAL_COLOR } from '../../lib/modelBuilder/constants';
import blackUS from '../../assets/images/ribbons/cabinetRibbon/upperShelf/black.png';
import naturalUS from '../../assets/images/ribbons/cabinetRibbon/upperShelf/natural.png';
import RibbonSection from '../Ribbon/RibbonSection';

interface StateProps {
  isVisible: boolean;
  furniture: Furniture | null;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectDisplayFurniturePanel(),
  furniture: makeSelectFurniture(),
});

function FurniturePanel() {
  useInjectReducer({ key: 'furniturePanel', reducer: reducer });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isVisible, furniture } = useSelector(stateSelector);
  const [isDragging, setDragging] = useState(false);
  const [dragMode, setDragMode] = useState(true);
  const [isRemoveModalOpen, showRemoveModal] = useState(false);
  const [cabinetToRemove, setCabinetToRemove] = useState<Cabinet | undefined>(
    undefined,
  );
  const isMobile = useMedia({ maxWidth: 1023 });

  useEffect(() => {
    if (isMobile) {
      setDragMode(true);
    } else {
      setDragMode(false);
    }
  }, [dragMode, isMobile]);

  const onBeforeCapture = () => {
    setDragging(true);
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination || !furniture) {
      return;
    }

    if (result.destination.droppableId === 'reorder') {
      // Reordering
      dispatch(
        reorderingFurnitureAction(
          result.source.index,
          result.destination.index,
        ),
      );
    } else if (result.destination.droppableId === 'trash') {
      dispatch(removeCabinetAction(result.draggableId));
    } else if (result.destination.droppableId === 'duplicate') {
      const newFurniture = furniture.clone();
      const cabinet = newFurniture.get(result.draggableId);

      if (cabinet) {
        dispatch(
          updateCabinetSuccess(newFurniture.add(cabinet.makeCopy()), null),
        );
      }
    }

    setDragging(false);
  };

  const removeCabinet = (cabinet: Cabinet) => {
    setCabinetToRemove(cabinet);
    showRemoveModal(true);
    // dispatch(removeCabinetAction(cabinet.id));
  };

  const getUpperShelfImg = () => {
    if (!furniture || !furniture.upperShelf) return naturalUS;

    switch (furniture.upperShelf.color.reference) {
      case BLACK_COLOR:
        return blackUS;
      case NATURAL_COLOR:
        return naturalUS;
      default:
        return whiteUS;
    }
  };

  if (furniture) {
    const panelContent = (
      <div className="flex flew-row w-full">
        <div className="flex flex-row w-full px-2 xl:px-5">
          <DragDropContext
            onDragEnd={onDragEnd}
            onBeforeCapture={onBeforeCapture}
          >
            <div className="flex relative w-full">
              {dragMode && (
                <StyledDropArea className="absolute top-0 left-0 flex justify-center items-center w-40 -mt-40 ml-2">
                  {furniture.quantity > 1 && isDragging && (
                    <div className="flex justify-center items-center bg-gray-100 border-2 border-gray-400 p-3">
                      <Droppable droppableId="trash" direction="horizontal">
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} className="flex">
                            <div className="flex flex-col justify-center items-center">
                              <FiTrash className="h-8 w-8 text-red-dark" />
                              <div className="text-gray-700 mx-2 italic">
                                {t('Drop here to remove the cabinet')}
                              </div>
                            </div>
                            {/*{provided.placeholder}*/}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </StyledDropArea>
              )}
              <div className="list-cabinet">
                <Droppable droppableId="reorder" direction="horizontal">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} className="flex">
                      {furniture.cabinets
                        .sort((a, b) => a.order - b.order)
                        .map((cabinetEntry, index) => (
                          <Draggable
                            key={cabinetEntry.cabinet.id}
                            draggableId={cabinetEntry.cabinet.id}
                            index={index}
                            disableInteractiveElementBlocking={false}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div>
                                  <FurnitureItem
                                    cabinet={cabinetEntry.cabinet}
                                    furniture={furniture}
                                    dragMode={dragMode}
                                    short={
                                      furniture.cabinets.length > 5 || dragMode
                                    }
                                    isDragging={isDragging}
                                    removeCabinet={removeCabinet}
                                  />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
              {dragMode && (
                <StyledDropArea className="absolute top-0 right-0 flex justify-center items-center w-40 -mt-40">
                  {furniture.quantity > 1 && isDragging && (
                    <div className="flex justify-center items-center bg-gray-100 border-2 border-gray-400 p-3">
                      <Droppable droppableId="duplicate" direction="horizontal">
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} className="flex">
                            <div className="flex flex-col justify-center items-center">
                              <FiCopy className="h-8 w-8 text-gray-700" />
                              <div className="text-gray-700 mx-2 italic">
                                {t('Drop here to duplicate the cabinet')}
                              </div>
                            </div>
                            {/*{provided.placeholder}*/}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </StyledDropArea>
              )}
            </div>
          </DragDropContext>
          <Modal
            open={isRemoveModalOpen}
            onClose={() => {
              showRemoveModal(false);
            }}
            center
          >
            {cabinetToRemove && (
              <>
                <div className="flex items-center">
                  {cabinetToRemove.thumbnail && (
                    <Img src={cabinetToRemove.thumbnail} alt="cabinet" />
                  )}
                  <div className="">
                    {t('Do you really want to remove this cabinet ?')}
                  </div>
                </div>
                <div className="flex justify-end">
                  <ModalCancelButton
                    onClick={() => {
                      setCabinetToRemove(undefined);
                      showRemoveModal(false);
                    }}
                  >
                    {t('No')}
                  </ModalCancelButton>
                  <ModalConfirmButton
                    onClick={() => {
                      dispatch(removeCabinetAction(cabinetToRemove.id));
                      showRemoveModal(false);
                    }}
                  >
                    {t('Yes')}
                  </ModalConfirmButton>
                </div>
              </>
            )}
          </Modal>
        </div>
        {/*<div className="flex justify-end">*/}
        {/*  <div className="flex flex-col justify-center mx-3">*/}
        {/*    <RibbonSection title={t('Upper shelf')} className="w-32" border>*/}
        {/*      <BigSelector*/}
        {/*        imgSrc={getUpperShelfImg()}*/}
        {/*        imgAlt={*/}
        {/*          furniture?.upperShelf*/}
        {/*            ? furniture?.upperShelf?.base.name*/}
        {/*            : t('None')*/}
        {/*        }*/}
        {/*        title={*/}
        {/*          furniture?.upperShelf*/}
        {/*            ? furniture?.upperShelf?.color.name*/}
        {/*            : t('None')*/}
        {/*        }*/}
        {/*        handleClick={() => {*/}
        {/*          dispatch(setColorsPanelAction(ActivePanel.upperShelf));*/}
        {/*        }}*/}
        {/*      />*/}
        {/*    </RibbonSection>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    );

    if (isMobile && isVisible) {
      return <FurniturePanelMobile>{panelContent}</FurniturePanelMobile>;
    } else {
      return (
        <Panel isVisible={isVisible} animation={false}>
          <StyledFurniturePanel dragMode={dragMode}>
            {/*<div className="flex flex-col self-end mb-2">
            <StyledReorderingBtn
              onClick={() => {
                setDragMode(!dragMode);
              }}
              className="px-3 py-2 mr-8 border border-gray-700  flex items-center"
              dragMode={dragMode}
            >
              <span className="mr-2">
                <FaSort />
              </span>
              {dragMode ? t('Stop reordering') : t('Reordering')}
            </StyledReorderingBtn>
          </div>*/}

            {/*<div className="self-start flex flex-col sethidden lg:block">
            <FurnitureName furnitureName={furniture.name} />
            // <div className="text-sm italic">
            //   You can reorder the cabinet by dragging the icon
            // </div>
          </div>*/}

            {panelContent}
          </StyledFurniturePanel>
        </Panel>
      );
    }
  } else return null;
}

export default FurniturePanel;

const StyledFurniturePanel = styled.div<{ dragMode: boolean }>`
  display: flex;
  //justify-content: space-between;
  align-items: center;
  min-height: 100px;
  width: 100%;
  ${p =>
    p.dragMode &&
    css`
      //background-color: #62e59f;
    `}
`;

const StyledReorderingBtn = styled.button<{ dragMode: boolean }>`
  color: ${p => (p.dragMode ? p.theme.white : p.theme.gray700)}
    ${p =>
      p.dragMode &&
      css`
        background-color: ${p => p.theme.main};
      `};
`;

const StyledDropArea = styled.div`
  margin: 0 2rem;
`;
